import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { IStepProps } from '../interfaces/Step'
import useStepFormHook from './useStepFormHook'
import {
  ALERT_MESSAGES,
  AUTRE,
  DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS,
  NEE_USA_FILE_INDEX,
  SECOND_NEE_USA_FILE_INDEX,
  STATUS,
} from '../enums/common'
import { IUploadFormState, IUploadProps } from '../interfaces/Upload'
import { getCountriesValues } from '../services/utils'
import {
  Monaco,
  ResidesFrenchColonies,
  USA_COUNTRY_CODE,
} from '../components/step5/Options'
import { IStore } from '../interfaces/Store'
import useCountriesListHook from './useCountriesListHook'
import { step2InitialState } from '../store/State/UploadState'
import UserAddressHook from './UserAddressHook'

const useStep5Hook = (props: IStepProps) => {
  const folderData = useSelector((state: IStore) => state.folderReducer.folder)
  const { form, onSubmit } = useStepFormHook(props)
  const [state, setState] = useState<IUploadFormState[] | any[]>([])
  const [showSecondForm, setShowSecondForm] = useState(false)
  const { fiscalCountriesList, countriesStatus } = useCountriesListHook()
  const { watch } = form
  const { status: addressStatus, address } = UserAddressHook()
  const {
    fiscal_address_country,
    nationalite_americaine_2,
    nationalite_americaine,
    tax_country_other,
    is_nee_usa_2,
    is_nee_usa,
  } = folderData

  const watchFiscalAddressCountry: string = watch(
    `fiscal_address_country`,
    fiscal_address_country
  )
  const watchTaxCountryOther: string = watch(
    `tax_country_other`,
    tax_country_other
  )
  const watchNationaliteAmericaine: string = watch(
    `nationalite_americaine`,
    nationalite_americaine
  )
  const watchNationaliteAmericaine2: string = watch(
    `nationalite_americaine_2`,
    nationalite_americaine_2
  )
  const watchIsNeeUsa: string = watch(`is_nee_usa`, is_nee_usa)
  const watchIsNeeUsa2: string = watch(`is_nee_usa_2`, is_nee_usa_2)

  const residesFrenchColoniesCountryCodes = getCountriesValues(
    ResidesFrenchColonies
  )
  const monacoCountryCode = getCountriesValues(Monaco)
  const showMonaco = monacoCountryCode.includes(watchFiscalAddressCountry)
  const showResidesFrenchColonies = residesFrenchColoniesCountryCodes.includes(
    watchFiscalAddressCountry
  )
  const fiscalAddressCountryAutre = watchFiscalAddressCountry === AUTRE
  const otherCountriesCodes = getCountriesValues(fiscalCountriesList)
  const showOtherCountriesExceptUsa =
    fiscalAddressCountryAutre &&
    otherCountriesCodes.includes(watchTaxCountryOther) &&
    watchTaxCountryOther !== USA_COUNTRY_CODE

  const showNif =
    showMonaco || showResidesFrenchColonies || showOtherCountriesExceptUsa

  const validateFile = (index: number) => {
    if (state && !state[index]) {
      throw new Error('Index not found')
    }
    let valid = true

    const [file] = state[index].file
    if (!file) {
      valid = false
      setState((prevState) => {
        const indexPrevState = prevState[index]
        const { fileAlerts } = indexPrevState
        fileAlerts[0] = ALERT_MESSAGES.mandatory
        const newState = [...prevState]
        newState[index] = {
          ...indexPrevState,
          fileAlerts,
          validationButtonError: null,
        }
        return newState
      })
    }

    return valid
  }

  const validateFiles = () => {
    return state.every((file) => {
      if (!!file && file.status) return file.status === STATUS.validated
      return true
    })
  }

  const addState = useCallback(
    (index: number) => {
      if (!state[index]) {
        setState((prevState) => {
          const indexPrevState = prevState[index]
          const newState = [...prevState]
          const defaultState: IUploadFormState = { ...step2InitialState }
          defaultState.docInfo =
            DOCUMENT_TYPE_UPLOAD_FILES_FOR_STEPS[props.step][index.toString()]
          defaultState.docValue = index.toString()
          defaultState.toggleValidateButton = true
          newState[index] = {
            ...indexPrevState,
            ...defaultState,
          }

          return newState
        })
      }
    },
    [props.step]
  )

  const removeState = useCallback(
    (index: number) => {
      if (state[index]) {
        setState((prevState) => {
          const newState = [...prevState]
          newState[index] = null
          return newState
        })
      }
    },
    [state]
  )
  const nationaliteAmericaineFalse =
    (watchNationaliteAmericaine || watchNationaliteAmericaine2) === '1'
  const isNeeUsaTrue = nationaliteAmericaineFalse && watchIsNeeUsa === '0'
  const isNeeUsaTrue2 = nationaliteAmericaineFalse && watchIsNeeUsa2 === '0'

  useEffect(() => {
    if (!state[NEE_USA_FILE_INDEX] && isNeeUsaTrue) {
      addState(NEE_USA_FILE_INDEX)
    }
    if (!state[SECOND_NEE_USA_FILE_INDEX] && isNeeUsaTrue2) {
      addState(SECOND_NEE_USA_FILE_INDEX)
    }
    if (!isNeeUsaTrue && state[NEE_USA_FILE_INDEX]) {
      removeState(NEE_USA_FILE_INDEX)
    }
    if (!isNeeUsaTrue2 && state[SECOND_NEE_USA_FILE_INDEX]) {
      removeState(SECOND_NEE_USA_FILE_INDEX)
    }
  }, [])

  const stepUploadFileProps: IUploadProps = {
    state,
    setState,
    validateFile,
  }
  const handleNextStep = async (event: React.MouseEvent<HTMLButtonElement>) => {
    if (validateFiles()) {
      return
    }
    event.preventDefault()
    state.forEach((_item, index: number) => {
      if (state[index] && validateFile(index)) {
        setState((prevState) => {
          const indexPrevState = prevState[index]
          const newState = [...prevState]
          newState[index] = {
            ...indexPrevState,
            validationButtonError: ALERT_MESSAGES.validationButton,
          }
          return newState
        })
      }
    })
    await form.triggerValidation()
  }

  return {
    form,
    onSubmit,
    handleNextStep,
    showSecondForm,
    setShowSecondForm,
    showMonaco,
    showNif,
    fiscalCountriesList,
    countriesStatus,
    state,
    stepUploadFileProps,
    folderData,
    addState,
    removeState,
    addressStatus,
    address,
  }
}

export default useStep5Hook
